import React, { useCallback, useEffect, useState } from 'react';
import {
  deleteRefollowDraftMember,
  fetchRefollowMembers,
  formatStatsDisplayDate,
  memberEntityLabel,
  publishRefollowBatch,
} from '../../../utils/marketingApi';
import cm from '../CallManager.module.scss';
import s from './Refollow.module.scss';

const PAGE_SIZE = 20;

export default function RefollowDraftBatch({
  batch,
  expanded,
  onToggle,
  onPublished,
  onDraftEmpty,
  showToast,
  onOpenCustomer,
  refreshTick = 0,
}) {
  const [members, setMembers] = useState([]);
  const [memberPage, setMemberPage] = useState(1);
  const [memberMeta, setMemberMeta] = useState({ total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const loadMembers = useCallback(async (page = memberPage) => {
    if (!batch?.id) return;
    setLoading(true);
    try {
      const data = await fetchRefollowMembers(batch.id, { page, pageSize: PAGE_SIZE });
      setMembers(data?.items || []);
      setMemberMeta({
        total: Number(data?.total) || 0,
        total_pages: Math.max(1, Number(data?.total_pages) || 1),
      });
    } catch (e) {
      showToast?.(e.message || 'Could not load draft members', 'error');
      setMembers([]);
      setMemberMeta({ total: 0, total_pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [batch?.id, memberPage, showToast]);

  useEffect(() => {
    if (!expanded) {
      setMembers([]);
      setMemberPage(1);
      setMemberMeta({ total: 0, total_pages: 1 });
      return;
    }
    loadMembers(memberPage);
  }, [expanded, loadMembers, refreshTick, memberPage]);

  const handleRemove = async (row) => {
    if (!row?.id || busyId) return;
    setBusyId(row.id);
    try {
      const out = await deleteRefollowDraftMember(row.id);
      showToast?.('Removed from draft', 'success');
      if (out?.batch_deleted) {
        onDraftEmpty?.(batch.id);
        return;
      }
      await loadMembers();
    } catch (e) {
      showToast?.(e.message || 'Could not remove', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handlePublish = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      await publishRefollowBatch(batch.id);
      showToast?.('Draft published', 'success');
      onPublished?.();
    } catch (e) {
      showToast?.(e.message || 'Publish failed', 'error');
    } finally {
      setPublishing(false);
    }
  };

  const count = batch.draft_member_count ?? members.length ?? 0;
  const opsId = batch.source_batch_id;
  const opsDesc = (batch.source_batch_description || '').trim();

  return (
    <div className={s.draftBatchBlock}>
      <div className={s.draftRow}>
        <div className={s.draftRowMain}>
          <span className={s.draftRowRefollowId}>{`Refollow #${batch.id}`}</span>
          {opsId || batch.source_batch_name ? (
            <span className={s.draftRowSource}>
              {opsId ? `#${opsId}` : ''}
              {opsId && batch.source_batch_name ? ' · ' : ''}
              {batch.source_batch_name || ''}
            </span>
          ) : null}
          {opsDesc ? (
            <p className={s.draftRowDescription} title={opsDesc}>{opsDesc}</p>
          ) : null}
          <span className={s.draftRowMeta}>
            {count}
            {' customer'}
            {count === 1 ? '' : 's'}
          </span>
        </div>
        <button
          type="button"
          className={cm.secondaryBtn}
          onClick={() => onToggle(batch.id)}
        >
          {expanded ? 'Hide list' : 'View draft'}
        </button>
        <button
          type="button"
          className={s.draftPublishBtn}
          disabled={publishing || count < 1}
          onClick={handlePublish}
        >
          {publishing ? 'Publishing…' : 'Publish'}
        </button>
      </div>

      {expanded ? (
        <div className={`${cm.statsFilterPanel} ${s.draftMemberPanel}`}>
          {loading && !members.length ? (
            <div className={cm.loading}>Loading draft members…</div>
          ) : null}
          {!loading && members.length === 0 ? (
            <div className={cm.empty}>No customers in this draft.</div>
          ) : (
            <>
              <div className={cm.statsFilterTableWrap}>
                <table className={cm.statsFilterTable}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>ID</th>
                      <th>Scheduled</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((row) => {
                      const entity = memberEntityLabel(row);
                      const sched = row.scheduled_date
                        ? formatStatsDisplayDate(row.scheduled_date)
                        : '—';
                      const time = row.scheduled_time ? ` ${row.scheduled_time}` : '';
                      return (
                        <tr key={row.id}>
                          <td>
                            <button
                              type="button"
                              className={cm.statsFilterBatchLink}
                              onClick={() => onOpenCustomer?.(row)}
                            >
                              {row.name || '—'}
                            </button>
                          </td>
                          <td>{row.phone_e164 || '—'}</td>
                          <td>{entity.label}</td>
                          <td>{`${sched}${time}`}</td>
                          <td>
                            <button
                              type="button"
                              className={s.draftRemoveBtn}
                              disabled={busyId === row.id}
                              onClick={() => handleRemove(row)}
                            >
                              {busyId === row.id ? '…' : 'Remove'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {memberMeta.total_pages > 1 ? (
                <div className={cm.pagination}>
                  <button
                    type="button"
                    className={cm.pagerBtn}
                    disabled={memberPage <= 1 || loading}
                    onClick={() => setMemberPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </button>
                  <span>
                    Page {memberPage} of {memberMeta.total_pages}
                    {memberMeta.total ? ` · ${memberMeta.total} customers` : ''}
                  </span>
                  <button
                    type="button"
                    className={cm.pagerBtn}
                    disabled={memberPage >= memberMeta.total_pages || loading}
                    onClick={() => setMemberPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              ) : null}
              <p className={s.draftMemberHint}>
                Remove anyone you do not want, then Publish. Customers appear in Due today / Upcoming by scheduled date.
              </p>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
